import { Injectable } from '@angular/core';
import { ApiQuoteResponse, ApiCoverageDetail, IApiTraveler, IApiAddress, ApiCoverageGroups } from '@bhtp-client-api/base';
import { CoverageService } from '../../coverage-modal/coverage.service';
import { CoverageContainer } from '../models/coverages/coverage-container.model';

@Injectable()
export class QuoteHelper {

    constructor(private coverageService: CoverageService) { }

    // returns the total price for a list of quotes
    public getTotalPriceForQuotes(quotes: ApiQuoteResponse[]): number {
        if (quotes) {
            let total: number = 0;
            quotes.forEach((quote) => {
                total += this.getTotalPriceForQuote(quote);
            });

            return total;
        }

        return 0;
    }

    // returns the total price for an individual quote
    public getTotalPriceForQuote(quote: ApiQuoteResponse): number {
        if (quote) {
            return this.getTotalBaseAmountForQuote(quote) + this.getTotalFeesForQuote(quote) + this.getSelectedOptionalCoverageTotalForQuote(quote);
        }

        return 0;
    }

    // gets the total base quote amount for a list of quotes - this excludes fees and optional coverages
    public getTotalBaseAmountForQuotes(quotes: ApiQuoteResponse[]): number {
        if (quotes) {
            let totalBaseAmount: number = 0;
            quotes.forEach((quote) => {
                totalBaseAmount += this.getTotalBaseAmountForQuote(quote);
            });

            return totalBaseAmount;
        }

        return 0;
    }

    // gets the total base quote amount for an individual quote - this excludes fees and optional coverages
    public getTotalBaseAmountForQuote(quote: ApiQuoteResponse): number {
        if (quote && quote.baseQuoteAmount) {
            return Number(quote.baseQuoteAmount);
        }

        return 0;
    }

    // gets the total fees for a list of quotes
    public getTotalFeesForQuotes(quotes: ApiQuoteResponse[]): number {
        if (quotes) {
            let totalFees: number = 0;
            quotes.forEach((quote) => {
                totalFees += this.getTotalFeesForQuote(quote);
            });

            return totalFees;
        }

        return 0;
    }

    public updateCoverageFeesForQuote(quote: ApiQuoteResponse): void {
        let totalOptionalCoverageFees: number = 0;
        let selectedCoverages: ApiCoverageDetail[] = this.getSelectedOptionalCoverages([quote]);

       selectedCoverages.forEach((coverage) => {
            totalOptionalCoverageFees += coverage.fees;
        });

        if (!(totalOptionalCoverageFees >= 0)) {
            totalOptionalCoverageFees = 0;
        }

        quote.coverageFees = totalOptionalCoverageFees;
    }

    // gets the total fees for an individual quote
    public getTotalFeesForQuote(quote: ApiQuoteResponse): number {
        let totalFees: number = 0;

        if (quote) {
            this.updateCoverageFeesForQuote(quote);

            if (!quote.packageFee || !(quote.packageFee >= 0)) {
                quote.packageFee = 0;
            }

            totalFees += Number(quote.packageFee + quote.coverageFees);
            quote.totalFees = totalFees;
        }

        return totalFees;
    }

    // gets the total premium of selected optional coverages for a list of quotes
    public getSelectedOptionalCoverageTotalForQuotes(quotes: ApiQuoteResponse[]): number {
        if (quotes) {
            let optionalTotalAmount: number = 0;
            quotes.forEach((quote) => {
                optionalTotalAmount += this.getSelectedOptionalCoverageTotalForQuote(quote);
            });

            return optionalTotalAmount;
        }

        return 0;
    }

    // gets the total premium of selected optional coverages for an individual quote
    public getSelectedOptionalCoverageTotalForQuote(quote: ApiQuoteResponse): number {
        if (quote) {
            if (quote.coverages && quote.coverages.optional) {
                let totalOptionalCoverageAmount: number = 0;
                quote.coverages.optional.forEach((coverage) => {
                    if (this.coverageService.isCoverageSelectedWithAllData(coverage)) {
                        totalOptionalCoverageAmount += this.coverageService.getOptionalCoveragePremium(coverage);
                    }
                });

                return totalOptionalCoverageAmount;
            }
        }

        return 0;
    }

    /// Gets a list of optional coverages that are currently selected (for navigating back and forth between tabs)
    /// returns empty array if none are found
    public getSelectedOptionalCoverages(quotes: ApiQuoteResponse[]): ApiCoverageDetail[] {
        let coverages: ApiCoverageDetail[] = [];

        if (quotes) {
            quotes.forEach((quote) => {
                if (quote.coverages && quote.coverages.optional) {
                    quote.coverages.optional.forEach((coverage) => {
                        if (this.coverageService.isCoverageSelectedWithAllData(coverage)) {
                            coverages.push(coverage);
                        }
                    });
                }
            });
        }

        return coverages;
    }

    public getUnavailableEditCoverageMessage(currentCoverage: CoverageContainer, newCoverages: ApiCoverageGroups, quotePriceUpdated: boolean): string {
        let coverages: string[] = [];

        if (currentCoverage) {
            if (currentCoverage.includedCoverages) {
                currentCoverage.includedCoverages.forEach(c => {
                    if ((newCoverages.included && !newCoverages.included.some(nc => nc.ratingId === c.ratingId))) {
                        coverages.push(c.name);
                    }
                });
            }

            const newOptionalCoverages = [...newCoverages.optional];
            if (newCoverages.bonus) {
                newOptionalCoverages.push(...newCoverages.bonus);
            }

            if (currentCoverage.optionalCoverages) {
                currentCoverage.optionalCoverages.forEach(c => {
                    if (newOptionalCoverages && !newOptionalCoverages.some(nc => nc.ratingId === c.ratingId)) {
                        coverages.push(c.name);
                    }
                });
            }

            // See if there were coverages added back
            if (coverages.length === 0 && newOptionalCoverages && currentCoverage.optionalCoverages) {
                newOptionalCoverages.forEach(c => {
                    if (c.isSelected && !currentCoverage.optionalCoverages.some(oc => oc.ratingId === c.ratingId)) {
                        coverages.push(c.name);
                    }
                });
            }
        }

        return this.getCoverageMessage(coverages.join(','), quotePriceUpdated, false, false);
    }

    public getCoverageMessage(coverages: string, quotePriceUpdated: boolean, isForPurchase: boolean = false, showContactInfo: boolean = false): string {
        let message = 'for the plan you previously quoted or purchased has changed.';
        let preMessage = '';
        let andMessage = '';
        let coverageString = '';

        if (quotePriceUpdated) {
            preMessage = 'Pricing ';
        }

        if (coverages && coverages.length > 0) {
            coverageString = `${coverages} eligibility `;
        }

        if (quotePriceUpdated && coverages && coverages.length > 0) {
            andMessage = 'and ';
        }

        // Only show message if
        // #1. Coverage changed, no price change
        // #2. Coverage changed cuasing a price change
        if (coverageString.length > 0) {
            if (isForPurchase) {
                message += ' Please review your updated plan coverages carefully before you purchase.';
            } else {
                message += ' Please review your updated plan coverages carefully before continuing.';
            }

            if (showContactInfo) {
                message += ' If you have questions, please call 1-844-411-2487 (M-F, 7 am - 7 pm CST).';
            }

            return `Something changed. ${preMessage}${andMessage}${coverageString}${message}`;
        } else {
            return null;
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/shared/helpers/quote.helper.ts